import { FC, useCallback, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Stack, TextField, Typography } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import isNil from 'lodash.isnil'

import { SidePanelElementProps, useSidePanelContext } from '@clara/layout-next'
import { Button, GroupBase, Select } from '@clara/ui'
import { useT } from '@clara/locale'
import { isEmpty } from '@clara/utils'
import { AsyncState } from '@clara/hooks'

import { getErrorHelperText } from '@/utils/form'
import { useSnackbar } from '@/utils/use-snackbar'

import { getBankInfoSchema } from '../use-bankinfo-schema'
import { BankName, BankNameResponse } from '../../../api/get-bank-name'
import {
  BankAccountType,
  BankInfoResponse,
  COBankInfoPayload,
  CoIdentificationType,
  postBankInfo,
  UserInfoResponse,
} from '../../../api/bank-info'
import { IOption } from '../br/ted.form'
import { getSuccessBankInfoSidePanel } from '../success'
import { useReimbursementsInProgress } from '@/features/reimbursements/api/use-reimbursements-inprogress'

type Props = {
  getBankInfo: () => Promise<BankInfoResponse>
  bankInfo?: BankInfoResponse
  userInfoResponse?: AsyncState<UserInfoResponse>
  isManagerOrCompanyOwner?: boolean
  banksResponse: AsyncState<BankNameResponse>
}

export const ACCOUNT_TYPES: IOption[] = [
  { value: 'CHECKING', label: 'co.fields.accountType.checking' },
  { value: 'SAVINGS', label: 'co.fields.accountType.saving' },
]

export const IDENTIFICATION_TYPES: IOption[] = [
  { value: 'CITIZENSHIP_ID', label: 'co.fields.identificationTypes.cc' },
  { value: 'FOREIGNER_ID', label: 'co.fields.identificationTypes.ce' },
  { value: 'PASSPORT', label: 'co.fields.identificationTypes.passport' },
]

const initialValues = {
  name: '',
  accountNumber: '',
  accountType: '',
  identificationType: '',
  identificationNumber: '',
  bankKey: '',
}

export const CoBankInfoForm: FC<SidePanelElementProps & Props> = ({
  getBankInfo,
  bankInfo,
  userInfoResponse,
  banksResponse,
  isManagerOrCompanyOwner,
}) => {
  const { t } = useT('reimbursements')
  const sidePanel = useSidePanelContext()
  const schema = getBankInfoSchema(t)
  const showSnackbar = useSnackbar()
  const [saveButtonIsLoading, setSaveButtonIsLoading] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues: bankInfo?.uuid
      ? {
          name: userInfoResponse?.value?.name ?? '',
          accountNumber: bankInfo.accountNumber,
          accountType: bankInfo.accountType,
          identificationType: bankInfo.identificationType,
          identificationNumber: bankInfo.identificationNumber,
          bankKey: bankInfo.bankKey,
        }
      : initialValues,
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  })

  const updateBankInfo = useCallback(
    async (data: {
      accountNumber: string
      accountType: string
      identificationType: string
      identificationNumber: string
      bankKey: string
    }) => {
      const payload: COBankInfoPayload = {
        accountNumber: data.accountNumber,
        accountType: data.accountType as BankAccountType,
        identificationType: data.identificationType as CoIdentificationType,
        identificationNumber: data.identificationNumber,
        bankKey: data.bankKey,
      }
      setSaveButtonIsLoading(true)
      try {
        const res = await postBankInfo(payload, bankInfo?.uuid)
        if (!isNil(res)) {
          sidePanel.onOpen(
            getSuccessBankInfoSidePanel({
              method: 'BANK',
            }),
            0,
          )
          getBankInfo()
        }
      } catch (error) {
        // TODO: manage endpoint error codes
        showSnackbar(t('bankinfo.update.error'), {
          variant: 'error',
        })
      } finally {
        setSaveButtonIsLoading(false)
      }
    },
    [bankInfo?.uuid, getBankInfo, showSnackbar, sidePanel, t],
  )

  const { onSubmit } = useReimbursementsInProgress(updateBankInfo)

  return (
    <Stack>
      {isManagerOrCompanyOwner && !bankInfo && (
        <Typography
          variant="body1"
          sx={(theme) => ({
            color: theme.palette.v3.content.medium_emphasis,
            marginBottom: theme.spacing(5),
          })}
        >
          {t('bankinfo.sidepanel.coandmanagertitle')}
        </Typography>
      )}
      <Typography
        variant="body1"
        sx={(theme) => ({
          color: theme.palette.v3.content.medium_emphasis,
          marginBottom: theme.spacing(3),
        })}
      >
        {t('bankinfo.sidepanel.description')}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={'column'} gap={3} mb={7}>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('bankinfo.sidepanel.name.label')}
                variant="outlined"
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                size="small"
                fullWidth
                value={userInfoResponse?.value?.name ?? ''}
                disabled={userInfoResponse?.loading}
                InputProps={{
                  readOnly: true,
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="identificationType"
            render={({ field, fieldState: { error } }) => (
              <Select<IOption, false, GroupBase<IOption>>
                options={IDENTIFICATION_TYPES}
                label={t('co.fields.identificationType.label')}
                placeholder=""
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                getOptionLabel={(option) => t(option.label)}
                getOptionValue={(option) => option.value}
                value={
                  IDENTIFICATION_TYPES.find(
                    (identificationType) =>
                      identificationType.value === field.value,
                  ) || null
                }
                onChange={(newValue) => {
                  field.onChange(newValue?.value)
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="identificationNumber"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('co.fields.identificationNumber.label')}
                variant="outlined"
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                size="small"
                fullWidth
                disabled={false}
              />
            )}
          />
          <Controller
            control={control}
            name="accountNumber"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('co.fields.accountNumber.label')}
                variant="outlined"
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                size="small"
                fullWidth
                disabled={false}
              />
            )}
          />
          <Controller
            control={control}
            name="accountType"
            render={({ field, fieldState: { error } }) => (
              <Select<IOption, false, GroupBase<IOption>>
                options={ACCOUNT_TYPES}
                label={t('co.fields.accountType.label')}
                placeholder=""
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                getOptionLabel={(option) => t(option.label)}
                getOptionValue={(option) => option.value}
                value={
                  ACCOUNT_TYPES.find(
                    (accountType) => accountType.value === field.value,
                  ) || null
                }
                onChange={(newValue) => {
                  field.onChange(newValue?.value)
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="bankKey"
            render={({ field }) => (
              <Select<BankName, false, GroupBase<BankName>>
                options={banksResponse.value ?? []}
                label={t('ted.fields.bank.label')}
                placeholder=""
                getOptionLabel={(bank) => bank.name}
                getOptionValue={(bank) => bank.key}
                isLoading={banksResponse.loading}
                value={
                  banksResponse.value?.find(
                    (bank) => bank.key === field.value,
                  ) || null
                }
                onChange={(newValue) => {
                  field.onChange(newValue?.key)
                }}
              />
            )}
          />

          <Stack
            direction="row"
            gap={2}
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(3),
              left: theme.spacing(3),
              right: theme.spacing(3),
              bottom: 0,
              backgroundColor: theme.palette.common.white,
              zIndex: 1,
            })}
          >
            <Button
              type={'submit'}
              sx={{ width: '208px' }}
              isLoading={banksResponse.loading || saveButtonIsLoading}
              isDisabled={isEmpty(banksResponse.value) || !isDirty}
            >
              {t('bankinfo.sidepanel.save')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  )
}
